import type { ReactNode } from 'react';
import type { CompanionDto, StatBlock } from '../api/companions';
import type { AbilitySlot } from '../api/abilities';
import { PetFace } from '../pet3d/PetFace';

export type IdentityPanelProps = {
  companion: CompanionDto;
  /** The account's companions, for the switcher rows (the screen's own `useCompanions()` list). */
  companions: CompanionDto[];
  /** The companion's six ability slots; `undefined` while the slots query is still loading. */
  slots: AbilitySlot[] | undefined;
  /** The screen's existing identity block (ring + name + Thriving), rendered first. */
  identity: ReactNode;
  /** Opens CompanionPickerSheet — the same sheet the identity bar's name chevron opens. */
  onPickCompanion: () => void;
  /** Opens AbilitySlotsSheet. */
  onAbilities: () => void;
  /** Opens AllocateSheet. Only offered when `freePoints > 0` (LevelRing's "+N" is the other entry). */
  onAllocate: () => void;
  /** The screen's `SkillsLine` (R2) — rendered under the stat chips; absent when `skill_craft`
   *  is off. A node, not the data: `HabitatScreen` owns the skills query and the sheet it opens. */
  skillsLine?: ReactNode;
};

/** Display labels in `StatBlock` key order — the seven-stat roster (combat rebuild), never a
 *  subset, so a chip row always reads the same across a pet's whole life. */
const STAT_LABELS: { key: keyof StatBlock; label: string }[] = [
  { key: 'vitality', label: 'Vitality' },
  { key: 'power', label: 'Power' },
  { key: 'focus', label: 'Focus' },
  { key: 'guard', label: 'Guard' },
  { key: 'ward', label: 'Ward' },
  { key: 'speed', label: 'Speed' },
  { key: 'fortune', label: 'Fortune' },
];

const SLOT_COUNT = 6;

/**
 * THE IDENTITY PANEL (spec §4.5 Habitat: "left: identity (name, level ring, XP bar, Thriving), the
 * seven stats as chips, ability slots, companions") — the desk's left panel at studio3. Task 7
 * scoped it to the identity block alone; this fills the column with what the spec lists, all of it
 * numbers already on `CompanionDto`, the slots query, or the companions list — nothing invented.
 *
 * Same idiom as `Dashboard`: presentational, props from the screen's own truth, no queries inside.
 * Every action here is a second entry into a sheet the screen already owns (picker, ability slots,
 * allocate); the phone reaches each one elsewhere and is unchanged.
 */
export function IdentityPanel({
  companion, companions, slots, identity, onPickCompanion, onAbilities, onAllocate, skillsLine,
}: IdentityPanelProps) {
  const atCap = !(companion.xpToNext > 0);
  const filled = slots?.filter((s) => s.abilityCode != null).length;
  const slotsLabel = filled == null ? 'Ability slots' : `Ability slots, ${filled} of ${SLOT_COUNT} filled`;
  // Same rule as the identity bar's own name line: on a one-companion account the row is a plain
  // row, not a button — the picker would be a list of one, a choice that does not exist.
  const switchable = companions.length > 1;
  const rows = switchable ? companions : [companion];

  return (
    <div className="identity-panel">
      {identity}

      <div className="identity-xp">
        <p className="identity-xp-line">
          {atCap ? `Level ${companion.level} · Max level` : `Level ${companion.level} · ${companion.xp} / ${companion.xpToNext} XP`}
        </p>
        {!atCap && (
          <div
            className="identity-xp-bar"
            role="progressbar"
            aria-label="XP to next level"
            aria-valuemin={0}
            aria-valuemax={companion.xpToNext}
            aria-valuenow={companion.xp}
          >
            <i style={{ width: `${Math.min(100, Math.max(0, (companion.xp / companion.xpToNext) * 100))}%` }} />
          </div>
        )}
      </div>

      {companion.freePoints > 0 && (
        <button type="button" className="btn-primary identity-allocate" onClick={onAllocate}>
          Allocate {companion.freePoints} point{companion.freePoints === 1 ? '' : 's'}
        </button>
      )}

      <ul className="identity-stats" aria-label="Stats">
        {STAT_LABELS.map(({ key, label }) => (
          <li key={key} className="chip">
            <span className="identity-stat-label">{label}</span> <b>{companion.stats[key]}</b>
          </li>
        ))}
      </ul>

      {skillsLine}

      <button type="button" className="identity-slots pressable" aria-label={slotsLabel} onClick={onAbilities}>
        <span className="identity-slots-caption">
          {filled == null ? 'Ability slots' : `Ability slots · ${filled} of ${SLOT_COUNT}`}
        </span>
        <span className="identity-pips" aria-hidden="true">
          {Array.from({ length: SLOT_COUNT }, (_, i) => {
            const s = slots?.[i];
            const cls = s?.abilityCode != null ? 'identity-pip-filled' : 'identity-pip-empty';
            return <i key={i} className={cls} />;
          })}
        </span>
      </button>

      <ul className="identity-companions" aria-label="Companions">
        {rows.map((c) => {
          const inDen = c.id === companion.id;
          const body = (
            <>
              <PetFace className="identity-companion-face" species={c.speciesCode} stage={c.stage} outfit={c.outfit} alt="" />
              <span className="identity-companion-name">{c.name}</span>
              <span className="identity-companion-level">Lv {c.level}</span>
            </>
          );
          return (
            <li key={c.id}>
              {switchable ? (
                <button
                  type="button"
                  className="identity-companion-row pressable"
                  aria-current={inDen ? 'true' : undefined}
                  onClick={onPickCompanion}
                >
                  {body}
                </button>
              ) : (
                <div className="identity-companion-row" aria-current="true">{body}</div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
